import React, { useState, useRef, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';

const ChatPanel = ({ 
  conversation, 
  messages, 
  onSendMessage,
  onBack,
  currentUserId,
  userGender,
  hasActiveSubscription 
}) => {
  const [messageText, setMessageText] = useState('');
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const [showMediaOptions, setShowMediaOptions] = useState(false);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    messagesEndRef?.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const formatMessageTime = (timestamp) => {
    const date = new Date(timestamp);
    return date?.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true });
  };

  const handleSendMessage = () => {
    if (!messageText?.trim()) return;
    
    if (userGender === 'male' && !hasActiveSubscription) {
      alert('Premium subscription required to send messages. Please upgrade your account.');
      return;
    }

    onSendMessage({
      text: messageText,
      type: 'text',
      timestamp: new Date()
    });
    setMessageText('');
  };

  const handleKeyPress = (e) => {
    if (e?.key === 'Enter' && !e?.shiftKey) {
      e?.preventDefault();
      handleSendMessage();
    }
  };

  const emojis = ['😊', '😂', '❤️', '👍', '🔥', '😍', '🎉', '💯'];

  const handleEmojiSelect = (emoji) => {
    setMessageText(prev => prev + emoji);
    setShowEmojiPicker(false);
  };

  return (
    <div className="flex flex-col h-full bg-card">
      <div className="p-4 border-b border-border flex items-center gap-3">
        <button
          onClick={onBack}
          className="lg:hidden p-2 hover:bg-muted rounded-lg transition-smooth"
        >
          <Icon name="ArrowLeft" size={20} />
        </button>

        <div className="relative flex-shrink-0">
          <Image
            src={conversation?.avatar}
            alt={conversation?.avatarAlt}
            className={`w-10 h-10 md:w-12 md:h-12 rounded-full object-cover ${
              conversation?.photoBlurred ? 'blur-md' : ''
            }`}
          />
          {conversation?.isOnline && (
            <span className="absolute bottom-0 right-0 w-3 h-3 bg-success rounded-full border-2 border-card" />
          )}
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <h3 className="text-sm md:text-base font-heading font-semibold truncate">
              {conversation?.userName}
            </h3>
            {conversation?.isVerified && (
              <Icon name="BadgeCheck" size={16} color="var(--color-primary)" />
            )}
          </div>
          <p className="text-xs md:text-sm text-muted-foreground">
            {conversation?.isOnline ? 'Online' : `Last seen ${formatMessageTime(conversation?.lastSeen)}`}
          </p>
        </div>

        <div className="flex items-center gap-2">
          <button className="p-2 hover:bg-muted rounded-lg transition-smooth">
            <Icon name="Phone" size={20} />
          </button>
          <button className="p-2 hover:bg-muted rounded-lg transition-smooth">
            <Icon name="Video" size={20} />
          </button>
          <button className="p-2 hover:bg-muted rounded-lg transition-smooth">
            <Icon name="MoreVertical" size={20} />
          </button>
        </div>
      </div>
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        <div className="flex items-center justify-center">
          <div className="px-4 py-2 bg-warning/10 rounded-lg flex items-center gap-2">
            <Icon name="ShieldAlert" size={16} color="var(--color-warning)" />
            <p className="text-xs md:text-sm text-warning font-medium">
              Screenshots are disabled for privacy protection
            </p>
          </div>
        </div>

        <div className="flex items-center justify-center">
          <div className="px-4 py-2 bg-success/10 rounded-lg flex items-center gap-2">
            <Icon name="Lock" size={16} color="var(--color-success)" />
            <p className="text-xs md:text-sm text-success font-medium">
              End-to-end encrypted conversation
            </p>
          </div>
        </div>

        {messages?.map((message) => {
          const isOwnMessage = message?.senderId === currentUserId;
          return (
            <div
              key={message?.id}
              className={`flex ${isOwnMessage ? 'justify-end' : 'justify-start'}`}
            >
              <div className={`max-w-[75%] md:max-w-[60%] ${isOwnMessage ? 'order-2' : 'order-1'}`}>
                {message?.type === 'text' && (
                  <div
                    className={`px-4 py-2 rounded-lg ${
                      isOwnMessage
                        ? 'bg-primary text-primary-foreground'
                        : 'bg-muted text-foreground'
                    }`}
                  >
                    <p className="text-sm md:text-base break-words">{message?.text}</p>
                    <div className="flex items-center justify-end gap-1 mt-1">
                      <span className={`text-xs ${isOwnMessage ? 'text-primary-foreground/70' : 'text-muted-foreground'}`}>
                        {formatMessageTime(message?.timestamp)}
                      </span>
                      {isOwnMessage && (
                        <Icon
                          name={message?.status === 'read' ? 'CheckCheck' : 'Check'}
                          size={14}
                          color={message?.status === 'read' ? 'var(--color-primary)' : 'currentColor'}
                        />
                      )}
                    </div>
                  </div>
                )}

                {message?.type === 'image' && (
                  <div className="space-y-2">
                    {message?.requiresApproval && !message?.approved && (
                      <div className="px-3 py-2 bg-warning/10 rounded-lg">
                        <p className="text-xs text-warning font-medium">
                          Photo sharing requires approval
                        </p>
                      </div>
                    )}
                    <div className="relative rounded-lg overflow-hidden">
                      <Image
                        src={message?.imageUrl}
                        alt={message?.imageAlt}
                        className={`w-full h-auto ${message?.blurred ? 'blur-lg' : ''}`}
                      />
                      {message?.requiresApproval && !message?.approved && (
                        <div className="absolute inset-0 bg-background/80 flex items-center justify-center">
                          <Button variant="default" size="sm">
                            Approve to View
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {message?.type === 'disappearing' && (
                  <div
                    className={`px-4 py-2 rounded-lg border-2 ${
                      isOwnMessage
                        ? 'bg-primary/10 border-primary text-foreground'
                        : 'bg-warning/10 border-warning text-foreground'
                    }`}
                  >
                    <div className="flex items-center gap-2 mb-1">
                      <Icon name="Timer" size={14} color="var(--color-warning)" />
                      <span className="text-xs font-medium text-warning">
                        Disappears in {message?.expiresIn}
                      </span>
                    </div>
                    <p className="text-sm md:text-base break-words">{message?.text}</p>
                  </div>
                )}
              </div>
            </div>
          );
        })}

        {isTyping && (
          <div className="flex justify-start">
            <div className="px-4 py-2 bg-muted rounded-lg">
              <div className="flex gap-1">
                <span className="w-2 h-2 bg-muted-foreground rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                <span className="w-2 h-2 bg-muted-foreground rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                <span className="w-2 h-2 bg-muted-foreground rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
              </div>
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>
      <div className="p-4 border-t border-border">
        {userGender === 'male' && !hasActiveSubscription && (
          <div className="mb-3 p-3 bg-warning/10 rounded-lg flex items-center gap-2">
            <Icon name="Crown" size={18} color="var(--color-warning)" />
            <p className="text-xs md:text-sm text-warning font-medium flex-1">
              Premium subscription required to send messages
            </p>
            <Button variant="default" size="sm">
              Upgrade
            </Button>
          </div>
        )}

        <div className="flex items-end gap-2">
          <div className="relative">
            <button
              onClick={() => setShowMediaOptions(!showMediaOptions)}
              className="p-2 md:p-3 hover:bg-muted rounded-lg transition-smooth"
            >
              <Icon name="Paperclip" size={20} />
            </button>
            {showMediaOptions && (
              <>
                <div
                  className="fixed inset-0 z-[250]"
                  onClick={() => setShowMediaOptions(false)}
                />
                <div className="absolute bottom-full left-0 mb-2 w-48 bg-card rounded-lg shadow-warm-lg z-[300] overflow-hidden">
                  <button className="w-full px-4 py-3 flex items-center gap-3 hover:bg-muted transition-smooth">
                    <Icon name="Image" size={18} color="var(--color-primary)" />
                    <span className="text-sm">Send Photo</span>
                  </button>
                  <button className="w-full px-4 py-3 flex items-center gap-3 hover:bg-muted transition-smooth">
                    <Icon name="Timer" size={18} color="var(--color-warning)" />
                    <span className="text-sm">Disappearing Message</span>
                  </button>
                </div>
              </>
            )}
          </div>

          <div className="flex-1 relative">
            <textarea
              value={messageText}
              onChange={(e) => setMessageText(e?.target?.value)}
              onKeyPress={handleKeyPress}
              placeholder="Type a message..."
              rows={1}
              className="w-full px-4 py-2 md:py-3 bg-muted rounded-lg text-sm md:text-base resize-none focus:outline-none focus:ring-2 focus:ring-primary transition-smooth"
              style={{ minHeight: '44px', maxHeight: '120px' }}
            />
          </div>

          <div className="relative">
            <button
              onClick={() => setShowEmojiPicker(!showEmojiPicker)}
              className="p-2 md:p-3 hover:bg-muted rounded-lg transition-smooth"
            >
              <Icon name="Smile" size={20} />
            </button>
            {showEmojiPicker && (
              <>
                <div
                  className="fixed inset-0 z-[250]"
                  onClick={() => setShowEmojiPicker(false)}
                />
                <div className="absolute bottom-full right-0 mb-2 p-3 bg-card rounded-lg shadow-warm-lg z-[300]">
                  <div className="grid grid-cols-4 gap-2">
                    {emojis?.map((emoji, index) => (
                      <button
                        key={index}
                        onClick={() => handleEmojiSelect(emoji)}
                        className="text-2xl hover:scale-125 transition-smooth"
                      >
                        {emoji}
                      </button>
                    ))}
                  </div>
                </div>
              </>
            )}
          </div>

          <Button
            variant="default"
            size="icon"
            onClick={handleSendMessage}
            disabled={!messageText?.trim() || (userGender === 'male' && !hasActiveSubscription)}
          >
            <Icon name="Send" size={20} color="#FFFFFF" />
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ChatPanel;